import LabelText from "@/components/label-text/label-text.vue";
import Loading from "@/components/loading/loading.vue";
import {LessonPlanLocale} from "@/locales/localeid";
import LessonPlanListState from "@/modules/lesson-plan-list";
import {
	FrontMatterURLDeleteModel,
	FrontMatterURLUploadModel,
	ILessonPlanVersionService,
	LessonPlanVersionService,
	SignatureResoureModel
} from "@/services/lesson-plan";
import {PdfFileSize, PdfFileType} from "@/util/constants";
import {ErrorType} from "@/util/enum";
import {
	AnonymousCredential,
	BlobServiceClient,
	BlockBlobClient,
	BlockBlobParallelUploadOptions,
	newPipeline
} from "@azure/storage-blob";
import {Icon, Progress, Upload} from "ant-design-vue";
import "ant-design-vue/dist/antd.less";
import {ActionItem, fmtMsg, MessageHelper, NotificationType} from "commonui";
import FileSaver from "file-saver";
import moment, {Moment} from "moment";
import {Component, Vue} from "vue-property-decorator";
import {getModule} from "vuex-module-decorators";

@Component({
	components: {
		Upload,
		ActionItem,
		LabelText,
		Progress,
		Icon,
		Loading
	}
})
export default class FrontMatter extends Vue {
	private lessonPlanListModule = getModule(LessonPlanListState);
	public supportedMediaType: string = ".pdf";
	public isDeleteVisible: boolean = true;
	public sasURL: SignatureResoureModel | null = null;
	public sasURLFetchedAt: Moment | null = null;
	public downloadSasURL: SignatureResoureModel | null = null;
	public downloadSasURLFetchedAt: Moment | null = null;
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();

	get frontMatterLabelText(): string {
		return fmtMsg(LessonPlanLocale.LessonPlanFrontMatterTitle);
	}

	checkIfFrontMatterURLExist() {
		return !!this.lessonPlanListModule.currentFrontMatterURL;
	}

	isCurrentLessonPlanVersionStatusPublished() {
		return !!this.lessonPlanListModule.currentLessonPlanVersion.status;
	}

	async getSasURL() {
		if (!this.sasURL || moment(this.sasURLFetchedAt).diff(moment(), "h") > 1) {
			this.setSasURL(await this.lessonPlanVersionService.getUploadSasURL());
		}
	}

	async getDownloadSasURL() {
		if (!this.downloadSasURL || moment(this.downloadSasURLFetchedAt).diff(moment(), "h") > 1) {
			this.setDownloadSasURL(await this.lessonPlanVersionService.getDownloadSasURL());
		}
	}

	getFilePath(data): string {
		const fileName = data.file.name;
		const {id: lessonPlanVersionId, versionGroupId} = this.lessonPlanListModule.currentLessonPlanVersion;
		const unit = this.lessonPlanListModule.currentUnit.id;
		const versionGroup = this.lessonPlanListModule.currentVersionGroups.find(v => v.id == versionGroupId)?.name;
		const filePath = `${versionGroup}/${lessonPlanVersionId}/${unit}/${fileName}`;
		this.lessonPlanListModule.setFrontMatterURL(filePath);
		return filePath;
	}

	setSasURL(value) {
		this.sasURL = value;
		this.sasURLFetchedAt = moment();
	}

	setDownloadSasURL(value) {
		this.downloadSasURL = value;
		this.downloadSasURLFetchedAt = moment();
	}

	async generateBlockBlobClient(args: { sasURL: string; container: string; path: string }): Promise<BlockBlobClient> {
		const { sasURL, container, path } = args;
		const pipeline = newPipeline(new AnonymousCredential(), {
			retryOptions: { maxTries: 4 } // Retry options
		});
		const serviceClient = new BlobServiceClient(sasURL, pipeline);
		const containerClient = serviceClient.getContainerClient(container);
		return containerClient.getBlockBlobClient(path);
	}

	async uploadFile(data) {
		if (this.checkBeforeUpload(data.file.type, data.file.size) == ErrorType.NoError) {
			this.lessonPlanListModule.setIsFrontMatterBeingUploaded(true);
			await this.getSasURL();
			const blobPath = await this.getFilePath(data);
			const blockBlobClient = await this.generateBlockBlobClient({
				sasURL: this.sasURL?.url as string,
				container: this.sasURL?.name as string,
				path: blobPath
			});
			const parallelOptions: BlockBlobParallelUploadOptions = {
				onProgress: ({ loadedBytes }) => {
					const currentProgress = (loadedBytes * 100) / data.file.size;
					this.lessonPlanListModule.setCurrentFrontMatterUploadProgress(Math.floor(currentProgress));
				}
			};
			await blockBlobClient.uploadData(data.file.originFileObj as File, parallelOptions).then((data) => {
				this.uploadFrontMatter();
			});
		} else if (this.checkBeforeUpload(data.file.type, data.file.size) == ErrorType.SizeError) {
			MessageHelper.Message(NotificationType.Warning, fmtMsg(LessonPlanLocale.FrontMatterFileSizeError));
			return false;
		} else if (this.checkBeforeUpload(data.file.type, data.file.size) == ErrorType.TypeError) {
			MessageHelper.Message(NotificationType.Warning, fmtMsg(LessonPlanLocale.FrontMatterFileTypeError));
		}
	}

	getfileUploadProgress() {
		return this.lessonPlanListModule.currentFrontMatterUploadProgress;
	}

	dummyRequest() {
		setTimeout(() => {}, 0);
	}

	async downloadFile(): Promise<File | undefined> {
		MessageHelper.Message(NotificationType.Success, fmtMsg(LessonPlanLocale.FrontMatterDownloadStartedMessage));
		await this.getDownloadSasURL();
		const blobPath = this.lessonPlanListModule.currentFrontMatterURL;
		const blockBlobClient = await this.generateBlockBlobClient({
			sasURL: this.downloadSasURL?.url as string,
			container: this.downloadSasURL?.name as string,
			path: blobPath
		});
		const request = await blockBlobClient.download();
		const blob = await request.blobBody;
		if (blob) {
			const fileType = this.supportedMediaType;
			FileSaver.saveAs(blob, `${this.lessonPlanListModule.currentFrontMatterFileName}`);
			MessageHelper.Message(NotificationType.Success, fmtMsg(LessonPlanLocale.FrontMatterDownloadSuccessMessage));
		} else {
			MessageHelper.Message(NotificationType.Success, fmtMsg(LessonPlanLocale.FrontMatterDownloadFailureMessage));
			throw "File not found.";
		}

		return undefined;
	}

	uploadFrontMatter() {
		const routeData = {
			lessonPlanVersionId: this.lessonPlanListModule.currentLessonPlanVersion.id,
			unitId: this.lessonPlanListModule.currentUnit.id
		};
		const body: FrontMatterURLUploadModel = {
			frontMatterFileUrl: this.lessonPlanListModule.currentFrontMatterURL
		};
		this.lessonPlanVersionService
			.putFrontMatterURL(body, routeData)
			.then((res) => {
				this.lessonPlanListModule.setIsFrontMatterBeingUploaded(false);
				MessageHelper.Message(
					NotificationType.Success,
					fmtMsg(LessonPlanLocale.FrontMatterUploadSuccessMessage)
				);
				this.lessonPlanListModule.setFrontMatterURL(body.frontMatterFileUrl);
				this.lessonPlanListModule.setCurrentFrontMatterUploadProgress(0);
			})
			.catch((e) => {
				this.lessonPlanListModule.setCurrentFrontMatterUploadProgress(0);
				this.lessonPlanListModule.setIsFrontMatterBeingUploaded(false);
				console.log(e);
			});
	}

	deleteFrontMatter() {
		MessageHelper.Confirm(
			fmtMsg(LessonPlanLocale.FrontMatterRemoveConfirmMessage),
			() =>
				new Promise((resolve, reject) => {
					{
						this.lessonPlanListModule.setIsFrontMatterBeingDeleted(true);
						const params: FrontMatterURLDeleteModel = {
							lessonPlanVersionId: this.lessonPlanListModule.currentLessonPlanVersion.id,
							unitId: this.lessonPlanListModule.currentUnit.id
						};
						this.lessonPlanVersionService
							.deleteFrontMatterURL(params)
							.then((data) => {
								this.lessonPlanListModule.setFrontMatterURL("");
								this.isDeleteVisible = false;
								this.lessonPlanListModule.setIsFrontMatterBeingDeleted(false);
								MessageHelper.Message(
									NotificationType.Success,
									fmtMsg(LessonPlanLocale.FrontMatterDeleteSuccessMessage)
								);
								resolve(true);
							})
							.catch((e) => {
								this.lessonPlanListModule.setIsFrontMatterBeingDeleted(false);
								console.log(e);
								reject();
							});
					}
				})
		);
	}

	checkBeforeUpload(fileType, fileSize) {
		if (fileType != PdfFileType) {
			return ErrorType.TypeError;
		}
		if (fileSize > PdfFileSize) {
			return ErrorType.SizeError;
		}

		return ErrorType.NoError;
	}
}
